import './Monitors.scss';
import { memo, useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import {
  Grid,
  GridItem,
  Heading,
  PendingContent,
  Space,
  Spinner,
  Tabs,
} from 'plume-ui';
import { HeadingStyles } from 'plume-ui/dist/components/Heading/Heading';
import { Tab } from 'plume-ui/dist/components/Tabs/Tabs';

import { MixPanelEvents } from '../../../mixPanelEvents';
import { useTrackEvent } from '../../trackingAnalytics/hooks/useTrackEvent';
import { AvailableScreens } from '../../trackingAnalytics/types';
import AutonomousMonitors from '../components/AutonomousMonitors/AutonomousMonitors';
import UserMonitors from '../components/UserMonitors/UserMonitors';
import useAutonomousMonitors from '../hooks/useAutonomousMonitors';
import { MonitorsTab } from '../types';
import { useRecoilValue } from 'recoil';
import { activePartnerAtom } from '../../../state';
import { useIsDevelopment } from '../../../hooks/useIsDevelopment';
import AccessNetworkMonitors from '../DEMO_AccessNetworkMonitors/AccessNetworkMonitors';

const Monitors = () => {
  const partner = useRecoilValue(activePartnerAtom);
  const { isDev } = useIsDevelopment();
  const { t } = useTranslation();
  const { tab } = useParams<{ tab: MonitorsTab }>();

  const history = useHistory();
  const location = useLocation();

  const {
    data: autonomousMonitors,
    isLoading: loadingAutoMonitors,
  } = useAutonomousMonitors();

  const trackEvent = useTrackEvent();

  useEffect(() => {
    trackEvent({
      eventName: MixPanelEvents.SCREEN,
      additionalContent: {
        SCREEN: AvailableScreens.Monitors,
      },
    });
  }, []);

  const TABS: Tab[] = useMemo(() => {
    let initialTabs = [
      {
        id: MonitorsTab.User,
        label: t('monitors.tabs.myMonitors'),
        content: <UserMonitors />,
      },
    ];

    if (isDev) {
      const accessNetworkPartnerIds = JSON.parse(
        import.meta.env.VITE_ACCESS_NETWORK_PARTNER_IDS || '[]',
      );
      const isAccessNetwork = accessNetworkPartnerIds.includes(partner?.id);
      if (isAccessNetwork) {
        initialTabs = [
          {
            id: MonitorsTab.AccessNetwork,
            label: 'Access network monitors',
            content: <AccessNetworkMonitors />,
          },
          ...initialTabs,
        ];
      }
    }

    if (!autonomousMonitors?.length) return initialTabs;
    return [
      {
        id: MonitorsTab.Autonomous,
        label: t('monitors.tabs.autonomousMonitors'),
        content: <AutonomousMonitors />,
      },
      ...initialTabs,
    ];
  }, [autonomousMonitors, partner, isDev]);

  const handleSelectTab = (option: Tab) => {
    const hasTab = location.pathname.includes(tab);
    const newPath = hasTab
      ? location.pathname.replace(tab, option.id)
      : `${location.pathname}/${option.id}`;
    history.push(newPath);
  };

  const activeTab = useMemo(() => {
    const selectedTab = TABS.findIndex((t) => t.id === tab);
    if (selectedTab >= 0) return selectedTab;
    return 0;
  }, [TABS, tab]);

  return (
    <PendingContent hideContent loading={loadingAutoMonitors} loader={Spinner}>
      <div className="Monitors">
        <Grid>
          <GridItem colSpan="12" tabletColSpan="12">
            <Space size="l" />
            <div className="Monitors__header">
              <div className="Monitors__header__left">
                <Heading
                  classes={(current: HeadingStyles) => ({
                    root: `${current.root} AppLayout__header`,
                  })}
                  size="xxxxl"
                  medium
                >
                  {t('monitors.title')}
                </Heading>
              </div>
            </div>
          </GridItem>

          {!loadingAutoMonitors && (
            <GridItem colSpan="12" tabletColSpan="12">
              <Tabs
                active={activeTab}
                classes={(styles) => ({
                  ...styles,
                  root: `${styles.root} Monitors__tabs`,
                })}
                options={TABS}
                tabType="flatTabs"
                onSelectTab={handleSelectTab}
              />
            </GridItem>
          )}
        </Grid>
      </div>
    </PendingContent>
  );
};

export default memo(Monitors);
